import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useToast } from '@/composables/common/useToast';
import { useQueryList as useListQuery } from '@/composables/common/useQueryList';

export function useCategoriasIndex() {
    const toast = useToast();

    const list = useListQuery({
        fetcher: async params => {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.index'),
                params
            );
            return res.data || { data: [], meta: {} };
        },
    });

    const listaId = list.listaId;

    const activarInvitacion = computed(() => store.getters.getFooter?.activar_compra_por_invitacion_bases || false);

    // --- Modal state ---

    const editItem = ref(null);
    const deleteItem = ref(null);

    function openEdit(item) {
        editItem.value = item;
    }

    function closeEdit() {
        editItem.value = null;
    }

    function openDelete(item) {
        deleteItem.value = item;
    }

    function closeDelete() {
        deleteItem.value = null;
    }

    // --- Columns ---

    const categoriasColumns = [
        { label: 'ID', field: 'id' },
        { label: 'Nombre', field: 'name' },
        { label: 'Precio', field: 'precio' },
        { label: 'Acciones', field: 'acciones' }
    ];

    // --- Delete ---

    async function borrar(categoriaId) {
        const params = {
            lista_id: listaId.value,
            categoria_id: categoriaId,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.borrar'),
                params
            );
            toast.success(res.data.message || 'Categoría eliminada');
            list.fetch();
            closeDelete();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al borrar la categoría');
        }
    }

    // --- Create ---

    const createLoading = ref(false);

    async function crear(formData) {
        createLoading.value = true;

        const params = {
            ...formData,
            indumentariasSelect: formData.indumentarias_ids
                ? formData.indumentarias_ids.map(id => ({ id }))
                : [],
            frecuencias: formData.frecuencia_ids
                ? formData.frecuencia_ids.map(id => ({ id }))
                : [],
            filters: {},
            lista_abonados_id: listaId.value,
            lista_id: listaId.value
        };
        delete params.indumentarias_ids;
        delete params.frecuencia_ids;

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.crear'),
                params
            );
            toast.success(res.data.message || 'Categoría creada correctamente');
            list.fetch();
            return true;
        } catch (error) {
            const message = error?.response?.data?.message || 'Error al crear la categoría';
            toast.error(message);
            return false;
        } finally {
            createLoading.value = false;
        }
    }

    // --- Edit ---

    async function editar(formData) {
        const params = {
            lista_id: listaId.value,
            name: formData.name,
            precio: formData.precio,
            observacion_indumentarias: formData.observacion_indumentarias,
            indumentariasSelect: formData.indumentarias_ids
                ? formData.indumentarias_ids.map(id => ({ id }))
                : [],
            activar_pago_plazos: formData.activar_pago_plazos,
            min_cantidad_plazos: formData.min_cantidad_plazos,
            max_cantidad_plazos: formData.max_cantidad_plazos,
            frecuencias: formData.frecuencia_ids
                ? formData.frecuencia_ids.map(id => ({ id }))
                : [],
            categoria_id: formData.id,
            representante_id: formData.representante_id,
            taquilla: formData.taquilla,
            online: formData.online,
            filters: {}
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.editar'),
                params
            );
            toast.success(res.data.message || 'Categoría actualizada correctamente');
            closeEdit();
            list.fetch();
        } catch (error) {
            const message = error?.response?.data?.message
                || error?.response?.data?.errors?.name?.toString()
                || error?.response?.data?.errors?.precio?.toString()
                || 'Error al actualizar la categoría';
            toast.error(message);
        }
    }

    // --- Toggle invitación ---

    async function toggleInvitacion(categoria) {
        const params = {
            lista_id: listaId.value,
            categoria_id: categoria.id,
            filters: {
                activar_compra_por_invitacion_bases: !categoria.activar_compra_por_invitacion_bases
            }
        };

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.categorias.editar'),
                params
            );
            toast.success(res.data.message || 'Configuración actualizada');
            list.fetch();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al actualizar');
        }
    }

    return {
        categorias: list.data,
        loading: list.loading,
        filters: list.filters,
        meta: list.meta,
        listaId,
        activarInvitacion,
        categoriasColumns,
        createLoading,
        fetch: list.fetch,
        borrar,
        crear,
        editar,
        toggleInvitacion,
        editItem,
        closeEdit,
        openEdit,
        deleteItem,
        closeDelete,
        openDelete,
        debouncedFetch: list.debouncedFetch,
        updateUrlQuery: list.updateUrlQuery,
        isHydrating: list.isHydrating,
    };
}
